import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { useCreateQuoteRevision } from "@/hooks/useCreateQuoteRevision";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface SaveQuoteRevisionButtonProps {
  leadId: string;
  leadStatus: string;
}

export const SaveQuoteRevisionButton = ({ leadId, leadStatus }: SaveQuoteRevisionButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [changeSummary, setChangeSummary] = useState("");
  const createRevision = useCreateQuoteRevision();

  // Only show button if status is quote_accepted or later
  if (!["quote_accepted", "invoicing", "executing", "reporting"].includes(leadStatus)) {
    return null;
  }

  const handleSaveRevision = () => {
    createRevision.mutate(
      { leadId, changeSummary },
      {
        onSuccess: () => {
          setShowDialog(false);
          setChangeSummary("");
        },
      }
    );
  };

  return (
    <>
      <Button 
        onClick={() => setShowDialog(true)}
        variant="outline"
        className="gap-2"
      >
        <Save className="h-4 w-4" />
        Save Revision
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Quote Revision</DialogTitle>
            <DialogDescription>
              Save the current state of the itinerary as a new revision. This creates a snapshot that can be compared to previous versions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="change-summary">What changed? (Optional)</Label>
              <Textarea
                id="change-summary"
                placeholder="e.g., Added extra night in Cape Town, changed hotel to preferred option"
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRevision}
              disabled={createRevision.isPending}
            >
              {createRevision.isPending ? "Saving..." : "Save Revision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
